import React, { useEffect } from "react"
import { Box, Button, Checkbox, Divider, FormControlLabel, TextField, Typography } from "@mui/material"
import { DateRangePickerSeeker } from "@ui-seeker/core"
import { AppPillToggle, secondaryButtonSx } from "@components"
import { dsColorNew } from "@palette"
import RemovableValueChip from "Pages/AutoEuropeTool/Page/@components/RemovableValueChip"
import { batch, useSignal } from "@preact/signals-react"
import { formatDate } from "@ui-seeker/utils"
import { parseBackendDate } from "@utils"
import { autoEuropeSeasonSetsDrawerData, autoEuropeSeasonSetsDrawerErrors } from "Pages/AutoEuropeTool/SeasonSets/@signals"

const errorTextSx = { fontSize: 12, color: dsColorNew.dangerRed }
const quickButtonSx = { ...secondaryButtonSx, height: 32, px: 1.5, fontSize: 13 }

const DAYS_MIN = 0
const DAYS_MAX = 365

function detectMode(r) {
	if (r.followingDays?.length > 0) return 'followingDays'
	if (r.dateRanges?.length > 0) return 'dateRanges'
	return 'nextSaturday'
}

function parseInitial(data) {
	const r = data.seasonRules ?? {}
	return {
		dateRanges: r.dateRanges ?? [],
		followingDays: r.followingDays ?? [],
		nextSaturday: Array.isArray(r.nextSaturday) ? r.nextSaturday : [],
		finalSeason: r.finalSeason ?? false,
		mode: detectMode(r),
	}
}

function clamp(v) {
	const n = parseInt(v, 10)
	if (isNaN(n)) return ""
	return Math.min(DAYS_MAX, Math.max(DAYS_MIN, n))
}

export default function AutoEuropeSeasonSetsRangesField() {
	const initial = parseInitial(autoEuropeSeasonSetsDrawerData.peek())
	const mode = useSignal(initial.mode)
	const dateRanges = useSignal(initial.dateRanges)
	const followingDays = useSignal(initial.followingDays)
	const nextSaturday = useSignal(initial.nextSaturday)
	const finalSeason = useSignal(initial.finalSeason)

	const pickerValue = useSignal(null)
	const dateOverlapError = useSignal(null)
	const fdFrom = useSignal("")
	const fdTo = useSignal("")
	const fdError = useSignal(null)

	const hasAnyRule = (
		dateRanges.value.length > 0 ||
		followingDays.value.length > 0 ||
		nextSaturday.value.length > 0 ||
		finalSeason.value
	)

	useEffect(() => {
		autoEuropeSeasonSetsDrawerErrors.value = {
			...autoEuropeSeasonSetsDrawerErrors.value,
			seasons: hasAnyRule,
		}
	}, [hasAnyRule])

	const syncAll = (dr, fd, ns, fs) => {
		autoEuropeSeasonSetsDrawerData.value = {
			...autoEuropeSeasonSetsDrawerData.value,
			seasonRules: {
				dateRanges: dr ?? dateRanges.peek(),
				followingDays: fd ?? followingDays.peek(),
				nextSaturday: ns ?? nextSaturday.peek(),
				finalSeason: fs ?? finalSeason.peek(),
			},
		}
	}

	const handleModeChange = value => batch(() => {
		mode.value = value
		dateRanges.value = []
		followingDays.value = []
		nextSaturday.value = []
		fdFrom.value = ""
		fdTo.value = ""
		fdError.value = null
		dateOverlapError.value = null
		pickerValue.value = null
		syncAll([], [], [], finalSeason.peek())
	})

	// ── Date ranges ──────────────────────────────────────────────────────────
	const handleDateRangeChange = val => {
		pickerValue.value = val
		if (!val || !val[0]) { dateOverlapError.value = null; return }
		if (Array.isArray(val) && val[0] && val[1]) {
			const from = formatDate(parseBackendDate(val[0]), { format: 'yyyy-MM-dd' })
			const to = formatDate(parseBackendDate(val[1]), { format: 'yyyy-MM-dd' })
			const overlaps = dateRanges.value.some(s => from <= s.to && s.from <= to)
			if (overlaps) {
				dateOverlapError.value = "Overlaps with an existing range"
				pickerValue.value = null
				return
			}
			batch(() => {
				dateOverlapError.value = null
				const updated = [...dateRanges.value, { from, to }].sort((a, b) => a.from.localeCompare(b.from))
				dateRanges.value = updated
				syncAll(updated, null, null, null)
				pickerValue.value = null
			})
		}
	}

	const removeDateRange = i => batch(() => {
		const updated = dateRanges.value.filter((_, j) => j !== i)
		dateRanges.value = updated
		syncAll(updated, null, null, null)
	})

	// ── Following days ───────────────────────────────────────────────────────
	const addFollowingDays = () => {
		const from = parseInt(fdFrom.value, 10)
		const to = parseInt(fdTo.value, 10)
		if (isNaN(from) || isNaN(to)) { fdError.value = "Enter both values"; return }
		if (from < DAYS_MIN || to > DAYS_MAX) { fdError.value = `Values must be ${DAYS_MIN}–${DAYS_MAX}`; return }
		if (from > to) { fdError.value = "From must be ≤ To"; return }
		const overlaps = followingDays.value.some(s => from <= s.to && s.from <= to)
		if (overlaps) { fdError.value = "Overlaps with an existing range"; return }
		batch(() => {
			fdError.value = null
			const updated = [...followingDays.value, { from, to }].sort((a, b) => a.from - b.from)
			followingDays.value = updated
			fdFrom.value = ""
			fdTo.value = ""
			syncAll(null, updated, null, null)
		})
	}

	const removeFollowingDay = i => batch(() => {
		const updated = followingDays.value.filter((_, j) => j !== i)
		followingDays.value = updated
		syncAll(null, updated, null, null)
	})

	// ── Next Saturday ────────────────────────────────────────────────────────
	const addSaturday = days => batch(() => {
		const updated = [...nextSaturday.value, days]
		nextSaturday.value = updated
		syncAll(null, null, updated, null)
	})

	const removeSaturday = i => batch(() => {
		const updated = nextSaturday.value.filter((_, j) => j !== i)
		nextSaturday.value = updated
		syncAll(null, null, updated, null)
	})

	// ── Final season ─────────────────────────────────────────────────────────
	const toggleFinalSeason = () => batch(() => {
		const updated = !finalSeason.value
		finalSeason.value = updated
		syncAll(null, null, null, updated)
	})

	return (
		<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
			{!hasAnyRule && (
				<Typography sx={errorTextSx}>Add at least one season rule</Typography>
			)}

			{/* Mode selector */}
			<AppPillToggle
				size="small"
				aria-label="Season type"
				value={mode.value}
				onChange={handleModeChange}
				options={[
					{ value: 'nextSaturday', label: 'Next Saturday' },
					{ value: 'dateRanges', label: 'Date Ranges' },
					{ value: 'followingDays', label: 'Following Days' },
				]}
				sx={{ alignSelf: 'flex-start' }}
			/>

			{/* Date Ranges */}
			{mode.value === 'dateRanges' && (
				<Box>
					<DateRangePickerSeeker
						label="Add date range"
						value={pickerValue.value}
						onChange={handleDateRangeChange}
						shouldDisableDate={() => false}
						error={dateOverlapError.value}
					/>
					{dateRanges.value.length > 0 && (
						<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
							{dateRanges.value.map((s, i) => (
								<RemovableValueChip key={i} onDelete={() => removeDateRange(i)}
									label={`${formatDate(parseBackendDate(s.from), { format: 'dd MMM yyyy' })} → ${formatDate(parseBackendDate(s.to), { format: 'dd MMM yyyy' })}`}
								/>
							))}
						</Box>
					)}
				</Box>
			)}

			{/* Following Days */}
			{mode.value === 'followingDays' && (
				<Box>
					<Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
						<TextField size="small" type="number" label="From" value={fdFrom.value}
							onChange={e => { fdFrom.value = clamp(e.target.value); fdError.value = null }}
							inputProps={{ min: DAYS_MIN }} sx={{ width: 80 }}
						/>
						<TextField size="small" type="number" label="To" value={fdTo.value}
							onChange={e => { fdTo.value = clamp(e.target.value); fdError.value = null }}
							onKeyDown={e => e.key === 'Enter' && addFollowingDays()}
							inputProps={{ min: DAYS_MIN }} sx={{ width: 80 }}
						/>
						<Button variant="outlined" onClick={addFollowingDays} sx={{ ...quickButtonSx, height: 37 }}>Add</Button>
					</Box>
					{fdError.value && (
						<Typography sx={{ ...errorTextSx, mt: 0.5 }}>{fdError.value}</Typography>
					)}
					{followingDays.value.length > 0 && (
						<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
							{followingDays.value.map((d, i) => (
								<RemovableValueChip key={i} label={`${d.from} → ${d.to} days`} onDelete={() => removeFollowingDay(i)}/>
							))}
						</Box>
					)}
				</Box>
			)}

			{/* Next Saturday */}
			{mode.value === 'nextSaturday' && (
				<Box>
					<Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
						<Button variant="outlined" onClick={() => addSaturday(7)} sx={quickButtonSx}>1 week</Button>
						<Button variant="outlined" onClick={() => addSaturday(14)} sx={quickButtonSx}>2 weeks</Button>
						<Button variant="outlined" onClick={() => addSaturday(28)} sx={quickButtonSx}>4 weeks</Button>
					</Box>
					{nextSaturday.value.length > 0 && (
						<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
							{(() => {
								const runs = []
								nextSaturday.value.forEach((d, i) => {
									const last = runs[runs.length - 1]
									if (last && last.d === d) { last.count++; last.lastIdx = i }
									else runs.push({ d, count: 1, lastIdx: i })
								})
								return runs.map((run, i) => {
									const weeks = run.d / 7
									const label = `${weeks} ${weeks === 1 ? 'week' : 'weeks'}${run.count > 1 ? ` ×${run.count}` : ''}`
									return <RemovableValueChip key={i} label={label} onDelete={() => removeSaturday(run.lastIdx)}/>
								})
							})()}
						</Box>
					)}
				</Box>
			)}

			<Divider sx={{ borderColor: dsColorNew.grayBorder }}/>

			{/* Final Season */}
			<FormControlLabel
				control={<Checkbox checked={finalSeason.value} onChange={toggleFinalSeason} size="small"/>}
				label="Final Season — first Saturday from 6 months to 1 year after the last range end"
				sx={{ '& .MuiFormControlLabel-label': { fontSize: 13, color: dsColorNew.textDark } }}
			/>
		</Box>
	)
}
